import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Logo } from '../components/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Play, 
  Clock, 
  Trophy,
  Flame,
  Target,
  Star,
  Calendar,
  ChevronRight,
  Medal,
  Bookmark,
  Heart,
  LogOut,
  User,
  Settings,
  Gift,
  Edit
} from 'lucide-react';

const ProfilePage = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [bookmarked, setBookmarked] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const [progressRes, historyRes, bookmarkedRes] = await Promise.all([
          api.get('/users/progress'),
          api.get('/users/history?limit=10'),
          api.get('/users/bookmarked')
        ]);

        setProgress(progressRes.data);
        setHistory(historyRes.data);
        setBookmarked(bookmarkedRes.data);
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [api]);

  const weeklyProgress = progress ? (progress.this_week_completed / progress.weekly_goal) * 100 : 0;

  // Lagree-specific achievement badges
  const badges = [
    { id: 'first_megaformer', name: 'First Megaformer Class', icon: <Star className="w-6 h-6" />, earned: (progress?.total_classes_completed || 0) >= 1, description: 'Complete your first Lagree class' },
    { id: 'spring_warrior', name: 'Spring Warrior', icon: <Target className="w-6 h-6" />, earned: (progress?.total_classes_completed || 0) >= 5, description: 'Master 5 classes on the springs' },
    { id: 'time_under_tension', name: 'Time Under Tension', icon: <Clock className="w-6 h-6" />, earned: (progress?.total_minutes || 0) >= 100, description: 'Spend 100 minutes in slow-burn movements' },
    { id: 'streak_master', name: '7-Day Streak Master', icon: <Flame className="w-6 h-6" />, earned: (progress?.longest_streak || 0) >= 7, description: 'Train for 7 consecutive days' },
    { id: 'megaformer_devotee', name: 'Megaformer Devotee', icon: <Trophy className="w-6 h-6" />, earned: (progress?.total_classes_completed || 0) >= 25, description: 'Complete 25 Lagree classes' },
    { id: 'lagree_legend', name: 'Lagree Legend', icon: <Medal className="w-6 h-6" />, earned: (progress?.total_classes_completed || 0) >= 50, description: 'Complete 50 Lagree classes' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">Home</Link>
            <Link to="/classes" className="text-zinc-400 hover:text-white transition-colors">Classes</Link>
            <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
            <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop</Link>
            <Link to="/instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</Link>
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold text-black">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                  <Gift className="w-4 h-4" />
                  Refer & Earn $50
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-10">
          <div className="w-24 h-24 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center text-3xl font-bold">
            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-1">{user?.first_name} {user?.last_name}</h1>
            <p className="text-zinc-500 mb-3">{user?.email}</p>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded-sm text-sm font-medium ${
                user?.subscription_status === 'active' 
                  ? 'bg-green-500/20 text-green-500' 
                  : 'bg-zinc-800 text-zinc-400'
              }`}>
                {user?.subscription_status === 'active' ? 'Premium Member' : 'Free Account'}
              </span>
              <span className="text-zinc-500 text-sm">
                Member since {new Date(user?.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <Button variant="outline" className="border-zinc-700">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            </Link>
            {user?.subscription_status !== 'active' && (
              <Link to="/subscription">
                <Button className="bg-teal-400 text-black hover:bg-teal-300">
                  Upgrade to Premium
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Trophy className="w-5 h-5" />
              <span className="text-xs uppercase tracking-wider">Total Classes</span>
            </div>
            <div className="text-3xl font-bold">{progress?.total_classes_completed || 0}</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Clock className="w-5 h-5" />
              <span className="text-xs uppercase tracking-wider">Total Minutes</span>
            </div>
            <div className="text-3xl font-bold">{progress?.total_minutes || 0}</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Flame className="w-5 h-5" />
              <span className="text-xs uppercase tracking-wider">Current Streak</span>
            </div>
            <div className="text-3xl font-bold text-orange-500">{progress?.current_streak || 0}</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Medal className="w-5 h-5" />
              <span className="text-xs uppercase tracking-wider">Longest Streak</span>
            </div>
            <div className="text-3xl font-bold">{progress?.longest_streak || 0}</div>
          </div>
        </div>

        {/* Weekly Goal */}
        <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">WEEKLY GOAL</h2>
            <span className="text-zinc-500">{progress?.this_week_completed || 0} / {progress?.weekly_goal || 3} classes</span>
          </div>
          <Progress value={Math.min(weeklyProgress, 100)} className="h-3" />
          {weeklyProgress >= 100 && (
            <p className="text-green-500 text-sm mt-2">Weekly goal achieved! Keep it up!</p>
          )}
        </div>

        {/* Badges */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6">ACHIEVEMENTS</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className={`p-4 rounded-sm text-center transition-colors ${
                  badge.earned 
                    ? 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30' 
                    : 'bg-zinc-900 border border-zinc-800 opacity-50'
                }`}
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  badge.earned ? 'bg-yellow-500/20 text-yellow-500' : 'bg-zinc-800 text-zinc-600'
                }`}>
                  {badge.icon}
                </div>
                <p className="text-sm font-medium">{badge.name}</p>
                {!badge.earned && <p className="text-xs text-zinc-600 mt-1">Locked</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Activity */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">RECENT ACTIVITY</h2>
            </div>
            
            {history.length === 0 ? (
              <div className="bg-[#121212] border border-zinc-800 rounded-sm p-8 text-center">
                <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="font-bold mb-2">No classes yet</h3>
                <p className="text-zinc-500 mb-4">Start your fitness journey today!</p>
                <Link to="/classes">
                  <Button className="bg-white text-black">Browse Classes</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((item) => (
                  <Link
                    key={item.completion_id}
                    to={`/class/${item.class?.id}`}
                    className="flex items-center gap-4 bg-[#121212] border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors"
                  >
                    <div className="w-16 h-16 bg-zinc-800 rounded-sm overflow-hidden flex-shrink-0">
                      <img
                        src={item.class?.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=200&auto=format&fit=crop'}
                        alt={item.class?.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold truncate">{item.class?.title}</h4>
                      <p className="text-zinc-500 text-sm">{item.class?.instructor_name}</p>
                      <p className="text-zinc-600 text-xs">
                        {new Date(item.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-zinc-600 text-sm">
                      {item.class?.duration_minutes} min
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Bookmarked Classes */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">SAVED CLASSES</h2>
            </div>
            
            {bookmarked.length === 0 ? (
              <div className="bg-[#121212] border border-zinc-800 rounded-sm p-8 text-center">
                <Bookmark className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="font-bold mb-2">No saved classes</h3>
                <p className="text-zinc-500 mb-4">Bookmark classes to watch later</p>
                <Link to="/classes">
                  <Button className="bg-white text-black">Explore Classes</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {bookmarked.map((cls) => (
                  <Link
                    key={cls.id}
                    to={`/class/${cls.id}`}
                    className="flex items-center gap-4 bg-[#121212] border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors"
                  >
                    <div className="w-16 h-16 bg-zinc-800 rounded-sm overflow-hidden flex-shrink-0">
                      <img
                        src={cls.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=200&auto=format&fit=crop'}
                        alt={cls.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold truncate">{cls.title}</h4>
                      <p className="text-zinc-500 text-sm">{cls.instructor_name}</p>
                      <span className={`text-xs difficulty-${cls.difficulty}`}>
                        {cls.difficulty}
                      </span>
                    </div>
                    <div className="text-zinc-600 text-sm">
                      {cls.duration_minutes} min
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Category Breakdown */}
        {progress?.classes_by_category && Object.keys(progress.classes_by_category).length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-bold mb-6">CLASSES BY CATEGORY</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(progress.classes_by_category).map(([category, count]) => (
                <div 
                  key={category}
                  className={`p-4 rounded-sm text-center badge-${category} bg-opacity-20`}
                >
                  <div className="text-2xl font-bold mb-1">{count}</div>
                  <div className="text-sm capitalize">{category.replace('_', ' ')}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ProfilePage;
